import { LookupScreeningOutcomeType, Patient } from "@/api/dtos";
import { AppSettings } from "@/AppSettings";
import ScreeningDialogContext from "@/contexts/ScreeningDialogContext";
import { faCheck, faTimes, faPaperPlane } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Chip, IconButton, styled, Tooltip, TooltipProps } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { IInstitution, OnlinePatientManagementContext, useSnackbar } from "@ngt/opms";
import { DateTime } from "luxon";
import { useCallback, useContext, useMemo } from "react";
import ConsentDialogContext, { ConsentDialogState } from "@/contexts/ConsentDialogContext";

const StyledChip = styled(Chip)(({theme}) => ({
    backgroundColor: '#987ba7',
    color: theme.palette.common.white,
}));

const StyledButton = styled(IconButton)(({theme}) => ({
    padding: theme.spacing(0),
    marginLeft: theme.spacing(2),
    color: theme.palette.mode === 'dark' ?
        `${AppSettings.palette.primaryDark} !important` :
        `${AppSettings.palette.primaryLight} !important`, 

    '&:hover': {
        color: theme.palette.mode === 'dark' ?
        `${theme.palette.primary.light} !important` :
        `${theme.palette.primary.dark} !important`, 
        background: 'none',
    },

    '&.Mui-disabled': {
        color: `${theme.palette.action.disabled} !important`,
    },

    '&:last-of-type': {
        marginRight: theme.spacing(0),
    }
}));

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ tooltip: className }} />
))(({ theme }) => ({
    
    backgroundColor: theme.palette.mode === 'dark' ?
        `rgba(0, 0, 0, 0.87)` :
        theme.palette.common.white,
    color: theme.palette.mode === 'dark' ?
        theme.palette.common.white :
        `rgba(0, 0, 0, 0.87)`,
    boxShadow: theme.shadows[1],
    fontSize: 11,
    
}));

const useScreeningPatientTableColumns = (
    institutions: IInstitution[] | null, 
    showInitials: boolean
) => {
    const { setPatientStudyNumber, setScreeningOutcome, setDialogOpen, dialogOpen } = useContext(ScreeningDialogContext);
    const { setPatientStudyNumber: setConsentPatientStudyNumber, setDialogState } = useContext(ConsentDialogContext);

    const onClick = useCallback((studyNumber: string | null, screeningOutcome: LookupScreeningOutcomeType | null) => {
        setPatientStudyNumber(studyNumber);
        setScreeningOutcome(screeningOutcome);
        setDialogOpen(true);
    }, [setPatientStudyNumber, setScreeningOutcome, setDialogOpen]);

    const {serviceStackClient} = useContext(OnlinePatientManagementContext);

    const { enqueueSnackbar } = useSnackbar();

    const onResendClick = useCallback((studyNumber: string | null) => {
        setConsentPatientStudyNumber(studyNumber);
        setDialogState(ConsentDialogState.ResendPip);
    }, [serviceStackClient, enqueueSnackbar, setDialogState, setConsentPatientStudyNumber]);
    
    const columns = useMemo<(GridColDef<Patient> | boolean)[]>(() => [
        {
            field: 'studyNumber',
            headerName: 'Temp. ID',
            width: 140
        },
        showInitials && {
            field: 'initials',
            headerName: 'Initials',
            //width: 200
        },
        {
            field: 'institutionCode',
            headerName: 'Site Code',
            renderCell: rowData => (
                <StyledChip
                    label={rowData.row.institutionCode}
                    size="small"
                />
            ),
            width: 110
        },
        {
            field: 'institutionId',
            headerName: 'Site Name',
            valueGetter: (_value, row) =>  institutions?.find(i => i.id === row.institutionId)?.name,
            flex: 1
        },
        {
            field: 'enteredDate',
            headerName: 'Date Entered',
            renderCell: ({row}) => (
                row.enteredDate ? DateTime.fromISO(row.enteredDate).toFormat('dd/MM/yyyy') : null
            ),
            width: 150
        },
        {
            field: 'id',
            headerName: '',
            filterable: false,
            sortable: false,
            renderCell: rowData => {
                return <>
                    <StyledTooltip title="Resend PIP">
                        <StyledButton
                            size="medium"
                            onClick={(e) => {onResendClick(rowData.row.studyNumber); e.stopPropagation();}}
                            disabled={!rowData.row.email}
                        >
                            <FontAwesomeIcon icon={faPaperPlane} fixedWidth />
                        </StyledButton>
                    </StyledTooltip>
                    <StyledTooltip title="Successful Screening">
                        <StyledButton
                            size="medium"
                            onClick={(e) => {onClick(rowData.row.studyNumber, LookupScreeningOutcomeType.Proceed); e.stopPropagation();}}
                        >
                            <FontAwesomeIcon icon={faCheck} fixedWidth />
                        </StyledButton>
                    </StyledTooltip>
                    <StyledTooltip title="Failed Screening">
                        <StyledButton
                            size="medium"
                            onClick={(e) => {onClick(rowData.row.studyNumber, LookupScreeningOutcomeType.Fail); e.stopPropagation();}}
                        >
                            <FontAwesomeIcon icon={faTimes} fixedWidth />{dialogOpen}
                        </StyledButton>
                    </StyledTooltip>
                </>
            },
            width: 160
        }
    ], [institutions]);

    return columns.filter(column => !!column) as GridColDef<Patient>[];
}

export default useScreeningPatientTableColumns;
